import type { Component } from '../../../services/aiConfigService';

/**
 * 树节点类型
 */
export interface TreeNode {
  key: string;
  title: string;
  type: 'service' | 'page' | 'component' | 'dataset' | 'field' | 'thinking-mode';
  data?: Component;
  children?: TreeNode[];
  isLeaf?: boolean;
}

/**
 * 构建组件树形结构
 * 结构：服务 -> 页面 -> 组件 -> 数据集 -> 字段/思考方式
 */
export const buildComponentTree = (components: Component[]): TreeNode[] => {
  // 构建服务 -> 页面 -> 组件的映射
  const serviceMap = new Map<string, Map<string, Component[]>>();
  
  components.forEach(component => {
    if (!serviceMap.has(component.serviceName)) {
      serviceMap.set(component.serviceName, new Map());
    }
    const pageMap = serviceMap.get(component.serviceName)!;
    if (!pageMap.has(component.pageId)) {
      pageMap.set(component.pageId, []);
    }
    pageMap.get(component.pageId)!.push(component);
  });

  const tree: TreeNode[] = [];
  
  serviceMap.forEach((pageMap, serviceName) => {
    const serviceNode: TreeNode = {
      key: `service:${serviceName}`,
      title: serviceName,
      type: 'service',
      children: [],
    };

    pageMap.forEach((pageComponents, pageId) => {
      const pageNode: TreeNode = {
        key: `page:${serviceName}:${pageId}`,
        title: pageId,
        type: 'page',
        children: [],
      };

      // 构建组件树（支持父子关系）
      const rootComponents = pageComponents.filter(c => !c.parentId);
      
      const buildComponentNode = (component: Component): TreeNode => {
        const componentNode: TreeNode = {
          key: `component:${component.id}`,
          title: component.name || component.componentId,  // 只显示组件名称，路径信息已在树形结构中体现
          type: 'component',
          data: component,
          children: [],
        };

        // 添加子组件
        const childComponents = pageComponents.filter(c => c.parentId === component.id);
        childComponents.forEach(child => {
          componentNode.children!.push(buildComponentNode(child));
        });

        // 添加关联的数据集
        if (component.datasets && component.datasets.length > 0) {
          component.datasets.forEach((cd: any) => {
            const dataset = cd.dataset;
            if (dataset) {
              const datasetNode: TreeNode = {
                key: `dataset:${dataset.datasetId}:${component.id}`,
                title: `${dataset.datasetId} (${dataset.name})`,
                type: 'dataset',
                children: [],
              };

              // 添加字段
              if (dataset.fields && dataset.fields.length > 0) {
                dataset.fields.forEach((field: any) => {
                  datasetNode.children!.push({
                    key: `field:${field.fieldId}:${dataset.datasetId}`,
                    title: `${field.fieldName} (${field.fieldType})`,
                    type: 'field',
                    isLeaf: true,
                  });
                });
              }

              // 添加思考方式
              if (dataset.thinkingModes && dataset.thinkingModes.length > 0) {
                dataset.thinkingModes.forEach((tm: any) => {
                  datasetNode.children!.push({
                    key: `thinking-mode:${tm.thinkingModeId}:${dataset.datasetId}`,
                    title: `${tm.thinkingModeId} (${tm.label})${tm.isDefault ? ' [默认]' : ''}`,
                    type: 'thinking-mode',
                    isLeaf: true,
                  });
                });
              }

              componentNode.children!.push(datasetNode);
            }
          });
        }

        return componentNode;
      };

      rootComponents.forEach(component => {
        pageNode.children!.push(buildComponentNode(component));
      });

      serviceNode.children!.push(pageNode);
    });

    tree.push(serviceNode);
  });

  return tree;
};
